import React, { useEffect, useMemo, useState } from 'react';

import CreatableSelect from 'react-select/creatable';

import { buildMemberOptions } from 'Helpers/approvalRecipients';
import { apiClient } from 'Helpers/network';

import Modal from 'CommonComponents/Modal';
import ModalFooter from 'CommonComponents/ModalFooter';
import ModalHeader from 'CommonComponents/ModalHeader';

import { useToast } from 'Hooks/useToast';

// A pragmatic email-shape check, mirroring ScheduleReportModal's chip input so a
// malformed free-text address is rejected inline rather than 422-ing on submit.
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Lift the creatable menu above the modal (z-60), matching ScheduleReportModal's
// timezone-select portal treatment.
const recipientSelectStyles = {
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
};

/**
 * Ad-hoc "Email Now" send (IB-673 phase 3 endpoint, phase 6 UI). An immediate,
 * one-off delivery of a report to chosen recipients — no recurring schedule.
 * Recipients can be picked from InBudget members or typed as free-text external
 * emails (an explicit share); the payload is a flat list of email strings, which
 * the backend validates as emails and gates on the report's own visibleTo().
 *
 * Generic over any registered ReportInterface: the caller passes the report key,
 * its supported formats, the current parameters snapshot and the recipient
 * member list. Reuses the shared Modal shell + the approval-recipient helper
 * rather than reinventing the picker.
 */
export default function EmailReportNowModal({
    modal,
    nonce = 0,
    reportKey,
    reportName,
    supportedFormats = ['xlsx'],
    recipientOptions = [],
    parameters = {},
    storeUrl,
    onSent,
}) {
    const toast = useToast();

    const memberOptions = useMemo(() => buildMemberOptions(recipientOptions), [recipientOptions]);

    const [recipients, setRecipients] = useState([]);
    const [formats, setFormats] = useState(supportedFormats);
    const [errors, setErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);

    // Reset the form on every open (nonce bumps per open) so a send never keeps
    // stale recipients/formats from the previous one.
    useEffect(() => {
        setRecipients([]);
        setFormats(supportedFormats);
        setErrors({});
    }, [nonce]);

    const close = () => modal.hide();

    const toggleFormat = (formatKey) => {
        setFormats((prev) => (prev.includes(formatKey) ? prev.filter((item) => item !== formatKey) : [...prev, formatKey]));
    };

    const selectedRecipients = recipients.map((email) => memberOptions.find((option) => option.value === email) || { label: email, value: email });

    const submit = () => {
        if (!storeUrl || !reportKey) {
            return;
        }

        const nextErrors = {};
        if (!recipients.length) {
            nextErrors.recipients = $t('Add at least one recipient.');
        }
        if (!formats.length) {
            nextErrors.formats = $t('Select at least one format.');
        }
        setErrors(nextErrors);
        if (Object.keys(nextErrors).length) {
            return;
        }

        setSubmitting(true);

        apiClient
            .post(storeUrl, {
                report_key: reportKey,
                parameters: parameters || {},
                formats,
                recipients,
            })
            .then(() => {
                toast.success($t('The report is being sent to the recipients.'));
                onSent?.();
                close();
            })
            .catch((error) => {
                const serverErrors = error.response?.data?.errors;
                if (serverErrors) {
                    setErrors(Object.fromEntries(Object.entries(serverErrors).map(([field, messages]) => [field, [].concat(messages)[0]])));
                }
                toast.error(error.response?.data?.message || $t('Failed to send the report. Please try again later.'));
            })
            .finally(() => setSubmitting(false));
    };

    return (
        <Modal modal={modal} modalIndexClass="tw-z-60">
            <ModalHeader title={$t('Email report')} close={close} />

            <div className="modal__form-container tw-space-y-4">
                {reportName ? <div className="tw-text-sm tw-text-charcoal-75">{$t('Emailing: {{name}}', { name: reportName })}</div> : null}

                <div className="tw-flex tw-flex-col tw-space-y-1.5">
                    <label className="text-input__label">{$t('Recipients')}</label>
                    <CreatableSelect
                        isMulti
                        className="react-select-container"
                        classNamePrefix="react-select"
                        placeholder={$t('Search members or add an email address')}
                        options={memberOptions}
                        value={selectedRecipients}
                        onChange={(selected) => setRecipients((selected || []).map((option) => option.value))}
                        isValidNewOption={(inputValue) => EMAIL_PATTERN.test((inputValue || '').trim())}
                        formatCreateLabel={(input) => `${$t('Add')} "${input}"`}
                        menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                        styles={recipientSelectStyles}
                    />
                    {errors.recipients && <div className="tw-text-red tw-text-sm">{errors.recipients}</div>}
                </div>

                <div className="tw-flex tw-flex-col tw-space-y-1.5">
                    <label className="text-input__label">{$t('Formats')}</label>
                    <div className="tw-flex tw-flex-wrap tw-gap-3">
                        {supportedFormats.map((formatKey) => (
                            <label key={formatKey} className="tw-flex tw-items-center tw-gap-1.5 tw-cursor-pointer">
                                <input
                                    type="checkbox"
                                    className="input-checkbox"
                                    checked={formats.includes(formatKey)}
                                    onChange={() => toggleFormat(formatKey)}
                                />
                                <span className="tw-text-sm tw-text-charcoal">{formatKey.toUpperCase()}</span>
                            </label>
                        ))}
                    </div>
                    {errors.formats && <div className="tw-text-red tw-text-sm">{errors.formats}</div>}
                </div>

                <ModalFooter
                    submitButtonVariant="primary"
                    cancelButtonText={$t('Cancel')}
                    primaryButtonText={$t('Send')}
                    onCancel={close}
                    handleSubmit={submit}
                    isSubmitting={submitting}
                />
            </div>
        </Modal>
    );
}
